// TeamFormDrawer — the create / edit SlideOver of a team (ADR-038's drawer: a simple
// create, a single-section edit). ONE drawer serves "New team" (team=null) and the
// page's "Edit team" (team=row): the name, and the clock — "Works on the project's
// clock" on by default, a zone picked when it is off (the family's time zone
// source). The lifecycle (disable · reactivate · delete) is the action door's,
// never a toggle here: a team's pause is a decision with consequences on the
// field, said in its own dialog. The edit rides the row stamp (ADR-084): the
// shell seeds from the team as it is at each open, sends the stamp and only what
// changed since, and a stale save shows the line with Load the latest.

import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import type { Team, TeamDetail } from '@ems-app/schemas/teams/responses';
import type { UpdateTeamRequest } from '@ems-app/schemas/teams/requests';
import { DrawerForm } from '../../components/patterns/forms/DrawerForm.js';
import { readLatest } from '../../components/patterns/forms/readLatest.js';
import { useFormShell } from '../../components/patterns/forms/useFormShell.js';
import { Picker } from '../../components/patterns/Picker.js';
import { Input } from '../../components/ui/Input.js';
import { Label } from '../../components/ui/Label.js';
import { Toggle } from '../../components/ui/Toggle.js';
import { useCreateTeam, useUpdateTeam } from '../../hooks/useTeamMutations.js';
import { fetchTeam, teamsKeys } from '../../hooks/useTeams.js';
import { timezoneSource } from '../../hooks/useTimezones.js';
import { teamFormSchema, type TeamFormValues } from './teamForm.js';

type TeamRow = Pick<TeamDetail, 'id' | 'name' | 'timezone_iana' | 'row_version'>;

export interface TeamFormDrawerProps {
  projectId: string;
  /** null = create; a team = edit. */
  team: TeamRow | null;
  open: boolean;
  onClose: () => void;
  /** After a create — the list navigates to the new team's page. */
  onCreated?: (team: Team) => void;
}

export function TeamFormDrawer({ projectId, team, open, onClose, onCreated }: TeamFormDrawerProps) {
  const { t } = useTranslation('teams');
  const qc = useQueryClient();
  const create = useCreateTeam(projectId);
  const update = useUpdateTeam(projectId);
  const isEdit = team !== null;

  const shell = useFormShell<TeamFormValues, TeamRow>({
    schema: teamFormSchema,
    row: team,
    values: (r) => ({ name: r?.name ?? '', project_clock: (r?.timezone_iana ?? null) === null, timezone_iana: r?.timezone_iana ?? null }),
    // The fresh team: a quiet read (a team gone meanwhile is said in the form), into the page's entry once it answers.
    reload: team ? () => readLatest(qc, teamsKeys.detail(projectId, team.id), () => fetchTeam(projectId, team.id, true)) : undefined,
    onSubmit: async (v, { stamp, changed }) => {
      const timezone_iana = v.project_clock ? null : v.timezone_iana;
      if (team) {
        // Only what changed since the drawer opened — never a diff against the live row (ADR-084 §6).
        const body: UpdateTeamRequest = { row_version: stamp };
        if ('name' in changed) body.name = v.name;
        if ('project_clock' in changed || 'timezone_iana' in changed) body.timezone_iana = timezone_iana;
        if (body.name !== undefined || body.timezone_iana !== undefined) await update.mutateAsync({ teamId: team.id, body });
      } else {
        const created = await create.mutateAsync({ name: v.name, timezone_iana });
        onCreated?.(created);
      }
    },
    onSuccess: onClose,
  });

  const { watch } = shell.form;
  const projectClock = watch('project_clock');
  const held = watch('timezone_iana');
  // Every zone the runtime knows, plus the one held (an edit always shows what it holds).
  const zones = useMemo(() => timezoneSource([held, team?.timezone_iana]), [held, team?.timezone_iana]);
  const e = shell.form.formState.errors;

  return (
    <DrawerForm shell={shell} open={open} onClose={onClose} title={isEdit ? t('form.editTitle') : t('form.createTitle')} submitLabel={isEdit ? t('form.save') : t('form.create')} autoFocusField="name">
      <div>
        <Label htmlFor="team-name" required>
          {t('form.name')}
        </Label>
        <Input id="team-name" {...shell.form.register('name')} error={e.name ? ' ' : undefined} />
      </div>
      <Controller control={shell.form.control} name="project_clock" render={({ field }) => <Toggle checked={field.value} onChange={(on) => field.onChange(on)} label={t('form.projectClock')} />} />
      {!projectClock && (
        <Controller
          control={shell.form.control}
          name="timezone_iana"
          render={({ field }) => (
            <Picker source={zones} label={t('form.timezone')} placeholder={t('form.timezonePlaceholder')} required value={field.value} onChange={(v) => field.onChange(v)} error={e.timezone_iana ? ' ' : undefined} />
          )}
        />
      )}
    </DrawerForm>
  );
}
